import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Download,
  Search,
  FileText,
  User,
  Award,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ChevronLeft,
  Edit3,
  Save,
  X,
  Clock,
  Filter,
} from "lucide-react";
import { useParams } from "react-router-dom";
import { getCourseGradebook } from "../../../../services/assignment.service";
import {
  getCourseGrading,
  bulkUpdateStudentGrading,
  getContinuousAssessmentPlan,
  publishGrades,
  unpublishGrades,
} from "../../../../services/course.service";
import { useCourse } from "../../../../context/CourseContext";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import {
  card,
  surfaceMuted,
  emptyState,
  dividerInner,
  pageTitle,
  cardTitle,
  microLabel,
  bodySecondary,
  statNumberXL,
  accentBarSection,
  accentBarCard,
  badgeBase,
  statusStyles,
  iconSize,
  motionFadeUp,
  motionStaggerChild,
  motionPageTransition,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";
import { Skeleton, SkeletonCard } from "../../../StudentDashboard/Components/ui/Skeleton";

// ---------------------------------------------------------------------------
// SKELETON LOADER
// ---------------------------------------------------------------------------

const GradebookSkeleton = () => (
  <div className="space-y-6 p-6">
    {/* Header skeleton */}
    <div>
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-1 w-12 mt-3" />
      <Skeleton className="h-4 w-48 mt-3" />
    </div>

    {/* Tab skeleton */}
    <div className="flex gap-4">
      <Skeleton className="h-10 w-40" />
      <Skeleton className="h-10 w-40" />
    </div>

    {/* Stat cards skeleton */}
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <SkeletonCard key={i} className="p-5 relative overflow-hidden">
          <Skeleton className="h-1 w-8 absolute top-0 left-0" />
          <Skeleton className="h-10 w-20 mt-2" />
          <Skeleton className="h-3 w-24 mt-3" />
        </SkeletonCard>
      ))}
    </div>

    {/* Search bar skeleton */}
    <Skeleton className="h-10 w-full" />

    {/* Table skeleton */}
    <SkeletonCard className="overflow-hidden">
      <div className={cn("px-4 py-3", surfaceMuted)}>
        <div className="flex gap-8">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-3 w-20" />
          ))}
        </div>
      </div>
      {[...Array(8)].map((_, i) => (
        <div
          key={i}
          className={cn(
            "flex gap-8 px-4 py-3.5",
            i > 0 && "border-t border-gray-200 dark:border-gray-700"
          )}
        >
          {[...Array(6)].map((_, j) => (
            <Skeleton key={j} className="h-4 w-20" />
          ))}
        </div>
      ))}
    </SkeletonCard>
  </div>
);

// ---------------------------------------------------------------------------
// BUTTON HELPERS
// ---------------------------------------------------------------------------

const btnBase =
  "flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-bold transition-colors";

const btnStyles = {
  primary: cn(btnBase, "bg-accent1 text-white hover:bg-accent1/90"),
  secondary: cn(
    btnBase,
    "border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
  ),
  danger: cn(btnBase, "bg-red-600 text-white hover:bg-red-700"),
  muted: cn(
    btnBase,
    "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
  ),
};

// ---------------------------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------------------------

const Gradebook = ({ courseID: propCourseID }) => {
  const { courseID: urlCourseID } = useParams();
  const { courseData } = useCourse();
  const courseID = propCourseID || urlCourseID || courseData?.id;
  const [gradebookData, setGradebookData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [activeSection, setActiveSection] = useState("assignments");
  const [expandedContinuous, setExpandedContinuous] = useState(false);
  const [isEditingSemester, setIsEditingSemester] = useState(false);
  const [semesterMarks, setSemesterMarks] = useState({});
  const [continuousAssessmentData, setContinuousAssessmentData] = useState([]);
  const [isPublished, setIsPublished] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  const currentSemester =
    courseData?.semNumber ||
    courseData?.semester?.semNumber ||
    "N/A";

  useEffect(() => {
    const fetchGradebook = async () => {
      if (!courseID) {
        setError("Course ID is missing");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await getCourseGradebook(courseID);
        setGradebookData(data);

        try {
          const categories = await getContinuousAssessmentPlan(courseID);
          if (categories && categories.length > 0) {
            const formattedCategories = categories.map((cat) => ({
              id: cat._id,
              _id: cat._id,
              category: cat.category,
              number: cat.number,
              eachMarks: cat.eachMarks,
              calculationMethod: cat.calculationMethod,
              n: cat.n,
              totalMarks: cat.totalMarks,
              selectedAssignments: cat.selectedAssignments || [],
            }));
            setContinuousAssessmentData(formattedCategories);
          }
        } catch (err) {
          console.error("Error fetching continuous assessment plan:", err);
        }

        try {
          const gradings = await getCourseGrading(courseID);
          if (gradings && gradings.length > 0 && data?.students) {
            const marksMap = {};
            gradings.forEach((grading) => {
              marksMap[grading.studentId] = {
                endTerm: grading.endTerm || 0,
                midTerm: grading.midTerm || 0,
                continuousEvaluation: grading.continuousEvaluation || {},
              };
            });
            const mergedMarks = {};
            data.students.forEach((student) => {
              mergedMarks[student.studentId] =
                marksMap[student.studentId] || {
                  endTerm: 0,
                  midTerm: 0,
                  continuousEvaluation: {},
                };
            });
            setSemesterMarks(mergedMarks);
            setIsPublished(gradings[0]?.isPublished || false);
          } else if (data?.students) {
            const initialMarks = {};
            data.students.forEach((student) => {
              initialMarks[student.studentId] = {
                endTerm: 0,
                midTerm: 0,
                continuousEvaluation: {},
              };
            });
            setSemesterMarks(initialMarks);
          }
        } catch (err) {
          console.error("Error fetching course grading:", err);
          if (data?.students) {
            const initialMarks = {};
            data.students.forEach((student) => {
              initialMarks[student.studentId] = {
                endTerm: 0,
                midTerm: 0,
                continuousEvaluation: {},
              };
            });
            setSemesterMarks(initialMarks);
          }
        }
      } catch (err) {
        console.error("Error fetching gradebook:", err);
        setError(
          err.response?.data?.message || "Failed to load gradebook"
        );
        toast.error("Failed to load gradebook data");
      } finally {
        setLoading(false);
      }
    };

    fetchGradebook();
  }, [courseID]);

  // Extract available branches
  const availableBranches = [
    ...new Set(
      (gradebookData?.students || []).map((s) => s.branch).filter(Boolean)
    ),
  ].sort();

  // Filter students
  const filteredStudents = useMemo(() => {
    return (
      gradebookData?.students?.filter((student) => {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          student.name?.toLowerCase().includes(searchLower) ||
          student.email?.toLowerCase().includes(searchLower) ||
          student.rollNo?.toLowerCase().includes(searchLower);
        const matchesBranch =
          !branchFilter || student.branch === branchFilter;
        return matchesSearch && matchesBranch;
      }) || []
    );
  }, [gradebookData?.students, searchTerm, branchFilter]);

  // Pagination derived values
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedStudents = filteredStudents.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);

  // Reset page on filter/tab change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, branchFilter, itemsPerPage, activeSection]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Status badge with vivid colors
  const getStatusBadge = (status, submissionDate = null) => {
    const statusMap = {
      graded: {
        style: "text-emerald-700 bg-emerald-50 border-emerald-200 dark:text-emerald-400 dark:bg-emerald-900/30 dark:border-emerald-700",
        label: "Graded",
      },
      submitted: {
        style: "text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-400 dark:bg-amber-900/30 dark:border-amber-700",
        label: "Submitted",
      },
      not_submitted: {
        style: "text-red-700 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-900/30 dark:border-red-700",
        label: "Not Submitted",
      },
    };
    const config = statusMap[status] || statusMap.not_submitted;
    return (
      <div className="flex flex-col items-center gap-0.5">
        <span className={cn(badgeBase, config.style)}>{config.label}</span>
        {submissionDate &&
          (status === "submitted" || status === "graded") && (
            <span
              className={cn(
                bodySecondary,
                "text-[10px] flex items-center gap-0.5"
              )}
            >
              <Clock className={iconSize.xs} />
              {new Date(submissionDate).toLocaleString()}
            </span>
          )}
      </div>
    );
  };

  // Export to Excel
  const exportToExcel = (section = "assignments") => {
    if (!gradebookData) return;

    let worksheet;
    let filename;

    if (section === "assignments") {
      const exportData = filteredStudents.map((student, index) => {
        const row = {
          "Sl.No": index + 1,
          "Roll No": student.rollNo || "—",
          Name: student.name || "—",
          Email: student.email || "—",
        };
        gradebookData.assignments.forEach((assignment) => {
          const grade = student.grades[assignment.id];
          row[assignment.title] =
            grade?.grade !== null && grade?.grade !== undefined
              ? grade.grade
              : "—";
          row[`${assignment.title} - Status`] =
            grade?.status || "not_submitted";
        });
        row["Total Score"] = student.totalScore || 0;
        row["Max Score"] = student.maxScore || 0;
        row["Percentage"] = student.percentage?.toFixed(2) || "0.00";
        row["Graded Count"] = student.gradedCount || 0;
        row["Total Assignments"] = student.totalAssignments || 0;
        return row;
      });
      worksheet = XLSX.utils.json_to_sheet(exportData);
      filename = `${gradebookData.course?.title || "Course"}_Assignment_Marks.xlsx`;
    } else {
      const exportData = filteredStudents.map((student, index) => ({
        "Sl.No": index + 1,
        "Roll No": student.rollNo || "—",
        Name: student.name || "—",
        Email: student.email || "—",
        "Total Score": student.totalScore || 0,
        "Max Score": student.maxScore || 0,
        Percentage: student.percentage?.toFixed(2) || "0.00",
        "Graded Assignments": student.gradedCount || 0,
        "Total Assignments": student.totalAssignments || 0,
        Semester: `Semester ${currentSemester}`,
      }));
      worksheet = XLSX.utils.json_to_sheet(exportData);
      filename = `${gradebookData.course?.title || "Course"}_Course_Grading.xlsx`;
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Gradebook");
    XLSX.writeFile(workbook, filename);
    toast.success("Gradebook exported successfully!");
  };

  // Class statistics
  const classStats = gradebookData?.students
    ? {
        totalStudents: gradebookData.students.length,
        averagePercentage:
          gradebookData.students.reduce(
            (sum, s) => sum + (s.percentage || 0),
            0
          ) / gradebookData.students.length || 0,
        totalAssignments: gradebookData.assignments?.length || 0,
        totalMaxScore:
          gradebookData.assignments?.reduce(
            (sum, a) => sum + (a.totalPoints || 0),
            0
          ) || 0,
      }
    : null;

  // Stat cards data
  const statCards = classStats
    ? [
        {
          label: "Total Students",
          value: classStats.totalStudents,
          icon: User,
        },
        {
          label: "Average %",
          value: `${classStats.averagePercentage.toFixed(1)}%`,
          icon: TrendingUp,
        },
        {
          label: "Assignments",
          value: classStats.totalAssignments,
          icon: FileText,
        },
        {
          label: "Total Points",
          value: classStats.totalMaxScore,
          icon: Award,
        },
      ]
    : [];

  // Table header cell helper
  const thClass = cn(microLabel, "py-3 px-4 text-left whitespace-nowrap");
  const thClassCenter = cn(microLabel, "py-3 px-4 text-center whitespace-nowrap");

  // Sticky column left offsets for Assignment Marks table
  // Sl.No(50) | Roll No(100) | Name(160) | Total(80) | Max(80) | %(90) | Graded(80)
  const stickyCol = {
    slNo:       "sticky left-0 z-10 min-w-[50px] w-[50px]",
    rollNo:     "sticky left-[50px] z-10 min-w-[100px] w-[100px]",
    name:       "sticky left-[150px] z-10 min-w-[160px] w-[160px]",
    total:      "sticky left-[310px] z-10 min-w-[80px] w-[80px]",
    max:        "sticky left-[390px] z-10 min-w-[80px] w-[80px]",
    percentage: "sticky left-[470px] z-10 min-w-[90px] w-[90px]",
    graded:     "sticky left-[560px] z-10 min-w-[80px] w-[80px]",
  };

  // Input style helper
  const inputClass = cn(
    "w-20 px-2 py-1 border rounded-sm text-center text-sm bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-accent1/30 focus:border-accent1",
    dividerInner
  );

  // Pagination footer component
  const PaginationFooter = () => {
    if (filteredStudents.length <= itemsPerPage) return null;
    return (
      <div
        className={cn(
          "p-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-t",
          dividerInner
        )}
      >
        <div className="flex items-center gap-4">
          <span className={bodySecondary}>
            {startIndex + 1}–{Math.min(endIndex, filteredStudents.length)} of{" "}
            {filteredStudents.length}
          </span>
          <div className="flex items-center gap-2">
            <span className={bodySecondary}>Show:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className={cn(
                surfaceMuted,
                "px-2 py-1 border rounded-sm text-gray-900 dark:text-white text-sm cursor-pointer",
                dividerInner
              )}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={cn(
              "p-2 rounded-sm border hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300",
              dividerInner
            )}
          >
            <ChevronLeft className={iconSize.md} />
          </button>
          <span className={cn(bodySecondary, "px-2")}>
            Page {currentPage} of {totalPages || 1}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages || totalPages === 0}
            className={cn(
              "p-2 rounded-sm border hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300",
              dividerInner
            )}
          >
            <ChevronRight className={iconSize.md} />
          </button>
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // LOADING / ERROR / EMPTY STATES
  // ---------------------------------------------------------------------------

  if (loading) {
    return <GradebookSkeleton />;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className={cn(emptyState, "border-red-200 dark:border-red-700")}>
          <X className={cn(iconSize.empty, "mx-auto text-red-500 mb-3")} />
          <p className="text-red-600 dark:text-red-400 font-bold">{error}</p>
        </div>
      </div>
    );
  }

  if (!gradebookData) {
    return (
      <div className="p-6">
        <div className={emptyState}>
          <FileText
            className={cn(
              iconSize.empty,
              "mx-auto text-gray-400 dark:text-gray-500 mb-3"
            )}
          />
          <p className="text-gray-600 dark:text-gray-400 font-bold">
            No gradebook data available
          </p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <motion.div
      {...motionFadeUp}
      className="relative -top-6 z-10 bg-gray-50 dark:bg-gray-900 min-h-screen"
    >
      <div className="p-6 space-y-6">
        {/* ── Header ──────────────────────────────────────────────── */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className={pageTitle}>
              {gradebookData.course?.title || "Course Gradebook"}
            </h1>
            <div className={cn(accentBarSection, "mt-3")} />
            <p className={cn(bodySecondary, "mt-3")}>
              {gradebookData.course?.courseCode || ""} &bull; Semester{" "}
              {currentSemester}
            </p>
          </div>
          <button
            onClick={() => exportToExcel(activeSection)}
            className={btnStyles.primary}
          >
            <Download className={iconSize.md} />
            Export to Excel
          </button>
        </div>

        {/* ── Section Tabs ────────────────────────────────────────── */}
        <div className={cn("flex gap-2 border-b", dividerInner)}>
          {[
            { key: "assignments", label: "Assignment Marks", icon: FileText },
            { key: "semester", label: "Course Grading", icon: Award },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveSection(tab.key)}
              className={cn(
                "px-6 py-3 text-sm font-bold uppercase tracking-wider transition-colors border-b-2",
                activeSection === tab.key
                  ? "text-accent1 border-accent1"
                  : "text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-800 dark:hover:text-gray-200"
              )}
            >
              <tab.icon className="w-4 h-4 inline-block mr-2" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Stat Cards ──────────────────────────────────────────── */}
        {statCards.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {statCards.map((stat, i) => (
              <motion.div
                key={stat.label}
                {...motionStaggerChild(i)}
                className={cn(card, "p-5 relative overflow-hidden")}
              >
                <div className={cn(accentBarCard, "absolute top-0 left-0")} />
                <div className="flex items-center justify-between mt-1">
                  <div>
                    <p className={statNumberXL}>{stat.value}</p>
                    <p className={cn(microLabel, "mt-2")}>{stat.label}</p>
                  </div>
                  <stat.icon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* ── Search / Filter Bar ─────────────────────────────────── */}
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by name, email, or roll number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={cn(
                "w-full pl-10 pr-4 py-2 border rounded-sm focus:ring-2 focus:ring-accent1/30 focus:border-accent1 dark:bg-gray-800 dark:text-white text-sm",
                dividerInner
              )}
            />
          </div>
          {availableBranches.length > 0 && (
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Filter className={cn(iconSize.md, "text-gray-400 dark:text-gray-500")} />
              </div>
              <select
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
                className={cn(
                  "pl-9 pr-8 py-2 border rounded-sm focus:ring-2 focus:ring-accent1/30 focus:border-accent1 dark:bg-gray-800 dark:text-white text-sm appearance-none cursor-pointer",
                  dividerInner
                )}
              >
                <option value="">All Branches</option>
                {availableBranches.map((branch) => (
                  <option key={branch} value={branch}>
                    {branch}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                <ChevronDown className={cn(iconSize.xs, "text-gray-400")} />
              </div>
            </div>
          )}
        </div>

        {/* ── Tab Content ─────────────────────────────────────────── */}
        <AnimatePresence mode="wait">
          {/* ── Assignment Marks Section ─────────────────────────── */}
          {activeSection === "assignments" && (
            <motion.div
              key="assignments"
              {...motionPageTransition}
              className={cn(card, "overflow-hidden")}
            >
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className={surfaceMuted}>
                    <tr>
                      {/* ── Fixed left columns ── */}
                      <th className={cn(thClass, stickyCol.slNo, surfaceMuted)}>
                        Sl.No
                      </th>
                      <th className={cn(thClass, stickyCol.rollNo, surfaceMuted)}>
                        Roll No
                      </th>
                      <th className={cn(thClass, stickyCol.name, surfaceMuted, "border-r", dividerInner)}>
                        Name
                      </th>
                      <th className={cn(thClassCenter, stickyCol.total, surfaceMuted, "bg-emerald-50 dark:bg-emerald-900/20")}>
                        Total
                      </th>
                      <th className={cn(thClassCenter, stickyCol.max, surfaceMuted, "bg-emerald-50 dark:bg-emerald-900/20")}>
                        Max
                      </th>
                      <th className={cn(thClassCenter, stickyCol.percentage, surfaceMuted, "bg-amber-50 dark:bg-amber-900/20")}>
                        %
                      </th>
                      <th className={cn(thClassCenter, stickyCol.graded, surfaceMuted, "border-r", dividerInner)}>
                        Graded
                      </th>
                      {/* ── Scrollable assignment columns ── */}
                      {gradebookData.assignments?.map((assignment) => (
                        <th
                          key={assignment.id}
                          className={cn(thClassCenter, "min-w-[140px]")}
                          title={assignment.title}
                        >
                          <div>
                            <div className="truncate max-w-[120px] mx-auto">{assignment.title}</div>
                            <div className="text-gray-500 dark:text-gray-400 text-xs mt-1 normal-case tracking-normal font-normal">
                              ({assignment.totalPoints} pts)
                            </div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {paginatedStudents.length === 0 ? (
                      <tr>
                        <td
                          colSpan={
                            7 + (gradebookData.assignments?.length || 0)
                          }
                          className="px-4 py-12"
                        >
                          <div className={emptyState}>
                            <Search
                              className={cn(
                                iconSize.empty,
                                "mx-auto text-gray-400 dark:text-gray-500 mb-3"
                              )}
                            />
                            <p className="font-bold text-gray-700 dark:text-gray-300">
                              No students found
                            </p>
                            <p className={cn(bodySecondary, "mt-1")}>
                              Try adjusting your search or filters
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedStudents.map((student, index) => (
                        <tr
                          key={student.studentId}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          {/* ── Fixed left columns ── */}
                          <td className={cn(stickyCol.slNo, "px-4 py-3 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white")}>
                            {startIndex + index + 1}
                          </td>
                          <td className={cn(stickyCol.rollNo, "px-4 py-3 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white font-medium")}>
                            {student.rollNo || "—"}
                          </td>
                          <td className={cn(stickyCol.name, "px-4 py-3 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white font-medium border-r", dividerInner)}>
                            {student.name || "—"}
                          </td>
                          <td className={cn(stickyCol.total, "px-4 py-3 text-center text-sm font-bold bg-emerald-50 dark:bg-emerald-900/20 text-gray-900 dark:text-white")}>
                            {student.totalScore || 0}
                          </td>
                          <td className={cn(stickyCol.max, "px-4 py-3 text-center text-sm font-bold bg-emerald-50 dark:bg-emerald-900/20 text-gray-900 dark:text-white")}>
                            {student.maxScore || 0}
                          </td>
                          <td className={cn(stickyCol.percentage, "px-4 py-3 text-center font-black bg-amber-50 dark:bg-amber-900/20 text-accent1")}>
                            {student.percentage?.toFixed(2) || "0.00"}%
                          </td>
                          <td className={cn(stickyCol.graded, "px-4 py-3 text-center text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-800 border-r", dividerInner)}>
                            {student.gradedCount || 0}/{student.totalAssignments || 0}
                          </td>
                          {/* ── Scrollable assignment columns ── */}
                          {gradebookData.assignments?.map((assignment) => {
                            const grade = student.grades[assignment.id];
                            const gradeValue = grade?.grade;
                            const status = grade?.status || "not_submitted";
                            const isLate = grade?.isLate || false;
                            const submissionDate =
                              grade?.submittedAt ||
                              grade?.submissionDate ||
                              null;

                            return (
                              <td
                                key={assignment.id}
                                className="px-4 py-3 text-center text-sm min-w-[140px]"
                              >
                                <div className="flex flex-col items-center gap-1">
                                  <span
                                    className={cn(
                                      "font-medium",
                                      gradeValue !== null &&
                                        gradeValue !== undefined
                                        ? "text-gray-900 dark:text-white"
                                        : "text-gray-400 dark:text-gray-500"
                                    )}
                                  >
                                    {gradeValue !== null &&
                                    gradeValue !== undefined
                                      ? gradeValue
                                      : "—"}
                                  </span>
                                  {isLate && (
                                    <span className="text-xs text-red-600 dark:text-red-400">
                                      Late
                                    </span>
                                  )}
                                  <div className="text-xs">
                                    {getStatusBadge(status, submissionDate)}
                                  </div>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <PaginationFooter />
            </motion.div>
          )}

          {/* ── Course Grading Section ───────────────────────────── */}
          {activeSection === "semester" && (
            <motion.div
              key="semester"
              {...motionPageTransition}
              className={cn(card, "overflow-hidden")}
            >
              {/* Section Header */}
              <div
                className={cn(
                  "p-6 border-b flex justify-between items-center",
                  dividerInner
                )}
              >
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className={cardTitle}>Course Grading (WILP Program)</h2>
                    {isPublished && (
                      <span className={cn(badgeBase, statusStyles.submitted)}>
                        Published
                      </span>
                    )}
                  </div>
                  <div className={cn(accentBarCard, "mt-2")} />
                  <p className={cn(bodySecondary, "mt-2")}>
                    End Term, Mid Term, and Continuous Evaluation marks
                  </p>
                </div>
                {!isEditingSemester ? (
                  <button
                    onClick={() => setIsEditingSemester(true)}
                    className={btnStyles.primary}
                  >
                    <Edit3 className={iconSize.md} />
                    Edit Marks
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={async () => {
                        try {
                          const gradings = await getCourseGrading(courseID);
                          if (
                            gradings &&
                            gradings.length > 0 &&
                            gradebookData?.students
                          ) {
                            const marksMap = {};
                            gradings.forEach((grading) => {
                              marksMap[grading.studentId] = {
                                endTerm: grading.endTerm || 0,
                                midTerm: grading.midTerm || 0,
                                continuousEvaluation:
                                  grading.continuousEvaluation || {},
                              };
                            });
                            const mergedMarks = {};
                            gradebookData.students.forEach((student) => {
                              mergedMarks[student.studentId] =
                                marksMap[student.studentId] || {
                                  endTerm: 0,
                                  midTerm: 0,
                                  continuousEvaluation: {},
                                };
                            });
                            setSemesterMarks(mergedMarks);
                          }
                        } catch (err) {
                          console.error("Error reloading grades:", err);
                        }
                        setIsEditingSemester(false);
                      }}
                      className={btnStyles.muted}
                    >
                      <X className={iconSize.md} />
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const gradingsToSave = Object.entries(
                            semesterMarks
                          ).map(([studentId, marks]) => ({
                            studentId,
                            endTerm: marks.endTerm || 0,
                            midTerm: marks.midTerm || 0,
                            continuousEvaluation:
                              marks.continuousEvaluation || {},
                          }));
                          await bulkUpdateStudentGrading(
                            courseID,
                            gradingsToSave
                          );
                          setIsEditingSemester(false);
                          toast.success("Semester marks saved successfully!");
                        } catch (err) {
                          console.error("Error saving semester marks:", err);
                          toast.error(
                            err.response?.data?.error ||
                              "Failed to save semester marks"
                          );
                        }
                      }}
                      className={btnStyles.primary}
                    >
                      <Save className={iconSize.md} />
                      Save
                    </button>
                    {!isPublished && (
                      <button
                        onClick={async () => {
                          if (
                            !confirm(
                              "Publish all grades? Students will be able to see their grades."
                            )
                          )
                            return;
                          try {
                            await publishGrades(courseID);
                            setIsPublished(true);
                            toast.success("Grades published successfully!");
                          } catch (err) {
                            console.error("Error publishing grades:", err);
                            toast.error(
                              err.response?.data?.error ||
                                "Failed to publish grades"
                            );
                          }
                        }}
                        className={btnStyles.primary}
                      >
                        <Award className={iconSize.md} />
                        Publish Grades
                      </button>
                    )}
                    {isPublished && (
                      <button
                        onClick={async () => {
                          if (
                            !confirm(
                              "Unpublish all grades? Students will no longer be able to see their grades."
                            )
                          )
                            return;
                          try {
                            await unpublishGrades(courseID);
                            setIsPublished(false);
                            toast.success("Grades unpublished successfully!");
                          } catch (err) {
                            console.error("Error unpublishing grades:", err);
                            toast.error(
                              err.response?.data?.error ||
                                "Failed to unpublish grades"
                            );
                          }
                        }}
                        className={btnStyles.danger}
                      >
                        <X className={iconSize.md} />
                        Unpublish
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className={surfaceMuted}>
                    <tr>
                      <th className={cn(thClass, "sticky left-0 z-10", surfaceMuted)}>
                        Sl.No
                      </th>
                      <th className={cn(thClass, "sticky left-12 z-10", surfaceMuted)}>
                        Roll No
                      </th>
                      <th className={cn(thClass, "sticky left-32 z-10", surfaceMuted)}>
                        Name
                      </th>
                      {!expandedContinuous && (
                        <>
                          <th className={cn(thClassCenter, "bg-accent1/5 dark:bg-accent1/10")}>
                            End Term
                          </th>
                          <th className={cn(thClassCenter, "bg-accent1/5 dark:bg-accent1/10")}>
                            Mid Term
                          </th>
                        </>
                      )}
                      {expandedContinuous &&
                      continuousAssessmentData.length > 0 ? (
                        <>
                          {continuousAssessmentData.map((category) => (
                            <th
                              key={category.id || category._id}
                              className={cn(thClassCenter, "bg-accent1/5 dark:bg-accent1/10")}
                            >
                              <div className="flex flex-col items-center">
                                <span className="text-xs">
                                  {category.category}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400 normal-case tracking-normal font-normal">
                                  ({category.totalMarks})
                                </span>
                              </div>
                            </th>
                          ))}
                          <th className={cn(thClassCenter, "bg-accent1/5 dark:bg-accent1/10")}>
                            <div className="flex items-center justify-center gap-2">
                              <span>Continuous Evaluation</span>
                              <button
                                onClick={() => setExpandedContinuous(false)}
                                className="text-accent1 hover:bg-accent1/10 rounded-sm transition-colors p-1"
                                title="Collapse"
                              >
                                <ChevronLeft className={iconSize.md} />
                              </button>
                            </div>
                          </th>
                        </>
                      ) : (
                        <th className={cn(thClassCenter, "bg-accent1/5 dark:bg-accent1/10")}>
                          <div className="flex items-center justify-center gap-2">
                            <span>Continuous Evaluation</span>
                            {continuousAssessmentData.length > 0 && (
                              <button
                                onClick={() => setExpandedContinuous(true)}
                                className="text-accent1 hover:bg-accent1/10 rounded-sm transition-colors p-1"
                                title="Expand"
                              >
                                <ChevronRight className={iconSize.md} />
                              </button>
                            )}
                          </div>
                        </th>
                      )}
                      {!expandedContinuous && (
                        <th className={cn(thClassCenter, "bg-accent1/5 dark:bg-accent1/10")}>
                          Total
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {paginatedStudents.length === 0 ? (
                      <tr>
                        <td
                          colSpan={
                            expandedContinuous
                              ? 3 + continuousAssessmentData.length + 1
                              : 7
                          }
                          className="px-4 py-12"
                        >
                          <div className={emptyState}>
                            <Search
                              className={cn(
                                iconSize.empty,
                                "mx-auto text-gray-400 dark:text-gray-500 mb-3"
                              )}
                            />
                            <p className="font-bold text-gray-700 dark:text-gray-300">
                              No students found
                            </p>
                            <p className={cn(bodySecondary, "mt-1")}>
                              Try adjusting your search or filters
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedStudents.map((student, index) => {
                        const studentMarks = semesterMarks[
                          student.studentId
                        ] || {
                          endTerm: 0,
                          midTerm: 0,
                          continuousEvaluation: {},
                        };
                        const continuousTotal = Object.values(
                          studentMarks.continuousEvaluation || {}
                        ).reduce(
                          (sum, val) => sum + (parseFloat(val) || 0),
                          0
                        );
                        const totalMarks =
                          (parseFloat(studentMarks.endTerm) || 0) +
                          (parseFloat(studentMarks.midTerm) || 0) +
                          continuousTotal;

                        return (
                          <tr
                            key={student.studentId}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                          >
                            <td className="sticky left-0 z-10 px-4 py-3 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white">
                              {startIndex + index + 1}
                            </td>
                            <td className="sticky left-12 z-10 px-4 py-3 bg-white dark:bg-gray-800 text-sm font-medium text-gray-900 dark:text-white">
                              {student.rollNo || "—"}
                            </td>
                            <td className="sticky left-32 z-10 px-4 py-3 bg-white dark:bg-gray-800 text-sm font-medium text-gray-900 dark:text-white">
                              {student.name || "—"}
                            </td>
                            {!expandedContinuous && (
                              <>
                                <td className="px-4 py-3 text-center text-sm bg-accent1/5 dark:bg-accent1/10">
                                  <input
                                    type="number"
                                    value={studentMarks.endTerm || 0}
                                    onChange={(e) => {
                                      setSemesterMarks((prev) => ({
                                        ...prev,
                                        [student.studentId]: {
                                          ...prev[student.studentId],
                                          endTerm:
                                            parseFloat(e.target.value) || 0,
                                        },
                                      }));
                                    }}
                                    className={cn(
                                      inputClass,
                                      !isEditingSemester &&
                                        "opacity-70 cursor-not-allowed"
                                    )}
                                    min="0"
                                    disabled={!isEditingSemester}
                                  />
                                </td>
                                <td className="px-4 py-3 text-center text-sm bg-accent1/5 dark:bg-accent1/10">
                                  <input
                                    type="number"
                                    value={studentMarks.midTerm || 0}
                                    onChange={(e) => {
                                      setSemesterMarks((prev) => ({
                                        ...prev,
                                        [student.studentId]: {
                                          ...prev[student.studentId],
                                          midTerm:
                                            parseFloat(e.target.value) || 0,
                                        },
                                      }));
                                    }}
                                    className={cn(
                                      inputClass,
                                      !isEditingSemester &&
                                        "opacity-70 cursor-not-allowed"
                                    )}
                                    min="0"
                                    disabled={!isEditingSemester}
                                  />
                                </td>
                              </>
                            )}
                            {expandedContinuous &&
                            continuousAssessmentData.length > 0 ? (
                              <>
                                {continuousAssessmentData.map((category) => {
                                  const categoryId =
                                    category.id || category._id;
                                  return (
                                    <td
                                      key={categoryId}
                                      className="px-4 py-3 text-center text-sm bg-accent1/5 dark:bg-accent1/10"
                                    >
                                      <input
                                        type="number"
                                        value={
                                          studentMarks.continuousEvaluation?.[
                                            categoryId
                                          ] || 0
                                        }
                                        onChange={(e) => {
                                          setSemesterMarks((prev) => ({
                                            ...prev,
                                            [student.studentId]: {
                                              ...prev[student.studentId],
                                              continuousEvaluation: {
                                                ...(prev[student.studentId]
                                                  ?.continuousEvaluation || {}),
                                                [categoryId]:
                                                  parseFloat(e.target.value) ||
                                                  0,
                                              },
                                            },
                                          }));
                                        }}
                                        className={cn(
                                          inputClass,
                                          !isEditingSemester &&
                                            "opacity-70 cursor-not-allowed"
                                        )}
                                        min="0"
                                        max={category.totalMarks}
                                        disabled={!isEditingSemester}
                                      />
                                    </td>
                                  );
                                })}
                                <td className="px-4 py-3 text-center text-sm bg-accent1/5 dark:bg-accent1/10">
                                  <span className="font-bold text-gray-900 dark:text-white">
                                    {continuousTotal.toFixed(2)}
                                  </span>
                                </td>
                              </>
                            ) : (
                              <td className="px-4 py-3 text-center text-sm bg-accent1/5 dark:bg-accent1/10">
                                <span className="font-bold text-gray-900 dark:text-white">
                                  {continuousTotal.toFixed(2)}
                                </span>
                              </td>
                            )}
                            {!expandedContinuous && (
                              <td className="px-4 py-3 text-center font-black text-accent1 bg-accent1/5 dark:bg-accent1/10">
                                {totalMarks.toFixed(2)}
                              </td>
                            )}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <PaginationFooter />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default Gradebook;
